import { APP_STORE_URL } from '../../components/AppStoreBadge';
import type { BlogPost } from '../types';

const CHECKED = '24 September 2026';

/**
 * Written for the Mealime shutdown (21 October 2026). The share flow was
 * tested end to end on an iPhone on the CHECKED date. Once Mealime is gone the
 * how-to stops working, so after the 21st this post should be rewritten as
 * "moving on from Mealime" or retired.
 */
const post: BlogPost = {
  slug: 'save-mealime-recipes',
  title: 'How to Save Your Mealime Recipes Before It Shuts Down',
  description:
    'Mealime closes on 21 October 2026 and has no export button. Here is how to move your recipes out one at a time, with ingredients, steps and macros.',
  category: 'How-to',
  publishedAt: '2026-09-24',
  updatedAt: '2026-09-24',
  readingMinutes: 3,
  author: { name: 'EasyToCook', role: 'The team behind the app' },
  ogImage: '/images/blog/save-mealime-recipes-og.png',
  coverImage: {
    src: '/images/blog/save-mealime-recipes-cover.webp',
    alt: 'The Mealime shutdown banner next to the same recipe saved in EasyToCook',
  },
  tags: ['Mealime', 'Mealime shutting down', 'export Mealime recipes', 'meal planning', 'recipe apps'],
  body: [
    {
      type: 'p',
      text:
        'Mealime is shutting down on **21 October 2026**. Your account and everything in it goes with it, and there is no export button for recipes, meal plans or grocery lists.',
    },
    {
      type: 'p',
      text:
        'We make EasyToCook, a recipe app, so we are biased. But we tested this on an iPhone and it works: you can share a recipe out of Mealime and get the whole thing back in EasyToCook. It is one recipe at a time, so start with the ones you actually cook.',
    },
    {
      type: 'quickAnswers',
      title: 'The short version',
      items: [
        { label: 'When', text: 'Mealime closes on **21 October 2026**. Do this before then.' },
        { label: 'How', text: 'Open a recipe in Mealime, tap **⋯**, then **Share**, then **EasyToCook**.' },
        { label: 'What comes across', text: 'Title, photo, ingredients, steps, servings and times. EasyToCook works out the macros.' },
        { label: 'Catch', text: 'iPhone and iPad only. One recipe at a time. The first 3 imports are free.' },
      ],
    },

    { type: 'h2', id: 'how-to', text: 'How to move a recipe out of Mealime' },
    {
      type: 'video',
      src: '/videos/blog/save-mealime-recipes.mp4',
      poster: '/images/blog/save-mealime-recipes/poster.jpg',
      label: 'Screen recording: sharing a Mealime recipe to EasyToCook and adding it to a shopping list',
      caption: 'The whole thing, sped up where the import is loading. Real time is about 15 seconds per recipe.',
    },
    {
      type: 'ol',
      items: [
        '**Get EasyToCook** from the [App Store](' + APP_STORE_URL + ') and sign in. It needs to be installed before it shows up in the share sheet.',
        '**Open a recipe in Mealime.** Anything from your meal plan or Favorites works.',
        'Tap the **⋯** button in the top right, then **Share**.',
        'Pick **EasyToCook** from the row of apps. If it is not there, scroll the row to the end, tap **More** and switch it on.',
        'You will see **Recipe started!** The recipe lands in your EasyToCook library about 15 seconds later, and you can keep sharing the next one while it works.',
      ],
    },
    {
      type: 'callout',
      tone: 'accent',
      title: 'Start with your Favorites',
      text:
        'If you have a lot of recipes, do the ones you cook every week first. Anything you have not saved by the 21st is gone.',
    },

    { type: 'h2', id: 'what-comes-across', text: 'What comes across (and what does not)' },
    {
      type: 'ul',
      items: [
        '**Comes across:** the title, photo, full ingredient list with amounts, every step, servings, prep and cook time, and a link back to the Mealime page.',
        '**Added by EasyToCook:** calories, protein, carbs and fat per serve, worked out from the ingredients. You can change the servings and the amounts scale.',
        '**One tap to a shopping list:** from the recipe, tap Shopping and every ingredient goes on your list, sorted by aisle.',
        '**Does not come across:** your Mealime meal plans, grocery lists and notes. Those have no share option, so screenshot anything you want to keep.',
      ],
    },

    { type: 'h2', id: 'imported-recipes', text: 'Recipes you added to Mealime yourself' },
    {
      type: 'p',
      text:
        'If you brought a recipe into Mealime from another website, the original page is still out there. Copy that link, tap **+** in EasyToCook and paste it in, or open the page in Safari and share it to EasyToCook the same way.',
    },

    { type: 'h2', id: 'honest-caveats', text: 'The honest caveats' },
    {
      type: 'ul',
      items: [
        '**iPhone and iPad only.** There is no Android app yet.',
        '**One at a time.** Nothing can bulk export Mealime, because Mealime has no export. Each recipe is one share.',
        '**3 free imports, then Pro.** After that it is a subscription, with a free trial on the yearly plan.',
        '**Only a handful of recipes?** Screenshots work fine and cost nothing.',
      ],
    },
    {
      type: 'cta',
      title: 'Keep the recipes you actually cook',
      text: 'Share a recipe from Mealime, a website, Instagram or TikTok, and EasyToCook turns it into a recipe with steps, macros and a shopping list. Free on iPhone.',
    },

    { type: 'h2', id: 'faq', text: 'Questions people ask' },
    {
      type: 'faq',
      items: [
        {
          question: 'When is Mealime shutting down?',
          answer:
            'On 21 October 2026, according to Mealime\'s own App Store listing. Until then, Mealime Pro is free for everyone and existing Pro subscriptions will not renew.',
        },
        {
          question: 'Can I export my Mealime recipes?',
          answer:
            'Not in bulk. Mealime has no export for recipes, meal plans or grocery lists. You can share recipes out one at a time, which is how the method above works.',
        },
        {
          question: 'Where is Mealime telling users to go?',
          answer:
            'To Meals Hub, a recipe and shopping tool inside Albertsons grocery apps such as Safeway and Vons. It needs an account with one of those stores.',
        },
        {
          question: 'Will Mealime recipe links still work after the 21st?',
          answer:
            'Probably not, so do not rely on them. Save the recipes you want before the shutdown.',
        },
        {
          question: 'Does this work on Android?',
          answer: 'Not yet. EasyToCook is on iPhone and iPad only.',
        },
      ],
    },

    {
      type: 'sources',
      items: [
        { label: 'Mealime on the App Store (shutdown notice in the release notes)', href: 'https://apps.apple.com/us/app/mealime-meal-plans-recipes/id1079999103', checked: CHECKED },
        { label: 'Mealime help: how to share recipes', href: 'https://support.mealime.com/article/101-how-to-share-recipes-grocery-list-meal-plans-mealime', checked: CHECKED },
        { label: 'Mealime help: importing a recipe from the web', href: 'https://support.mealime.com/article/112-importing-a-recipe-from-the-web', checked: CHECKED },
        { label: 'EasyToCook on the App Store', href: APP_STORE_URL, checked: CHECKED },
      ],
    },
  ],
};

export default post;
